import type { Feature, MapBrowserEvent, Map as OpenlayersMap } from 'ol'
import type { Extent } from 'ol/extent'
import VectorFeature, { type FeatureLike } from 'ol/Feature'
import type GeoJSON from 'ol/format/GeoJSON'
import type { Geometry } from 'ol/geom'
import { Vector } from 'ol/layer'
import { Vector as VectorSource } from 'ol/source'
import { Style } from 'ol/style'
import { Geo } from 'utils'

export enum Z_INDEX {
  BASE_MAP = 0,
  FIELD_POLYGONS = 1,
  /** Satellite raster, under the field outline it is clipped to. */
  SATELLITE_IMAGERY = 2,
  /** Soil polygons, under the field outline they are cut to. */
  SOIL_ZONES = 3,
  SECONDARY_DATA_LAYER = 4,
  PRIMARY_DATA_LAYER = 5,
  /** The readings themselves, over any surface derived from them. */
  SOIL_SAMPLES = 6,
  TRACTOR_POSITION = 7,
  /**
   * View-only overlay for the draw tool (helm preview lines, helm-inspect
   * edges/holes/corners, carved zones). Sits one slot below `DRAW_TOOL` so
   * drawn guidance primitives always render on top of the helm preview.
   */
  DRAW_TOOL_VIEW_ONLY = 8,
  DRAW_TOOL = 9,
  EVENT_CODE = 10,
  SCOUTING_NOTES = 11,
  /** Above SCOUTING_NOTES + 2, the highest slot the scouting layers take. */
  OPERATION_DEVICES = 14,
  LOCATION_INDICATOR = 100
}

/**
 * A base class for vector layers that allows for custom styling of features based on the data
 * they represent, and provides hooks for handling click and hover events on the layer.
 *
 * The goal of this class is to provide a common interface for all vector layers, so that they
 * do not need to interact with the openlayers api directly (which can often be confusing and poorly
 * typed).
 *
 * Avoid any conditional logic in this class based on subclass-specific properties, but instead
 * pass in the necessary logic as overrides to the constructor. This will allow for better
 * separation of concerns and easier maintenance of the code.
 *
 *
 */
export abstract class BaseVectorLayer<
  ItemData, // the data that each underlying feature represents
  Metadata extends MetaConstraint, // an object that holds metadata about the feature, useful for styling
  FeatureData = ItemData // the data associated with a given visible feature; only used when the feature data is different from the item data (e.g. in a cluster layer, the cluster has an array of ItemData)
> {
  protected source = new VectorSource({ wrapX: false })
  protected layer: Vector<VectorSource>
  protected extractData
  private readonly styleCache = new Map<string | number | boolean, Style>()

  constructor(
    protected map: OpenlayersMap,
    protected config: VectorLayerProps<ItemData, Metadata, FeatureData>,
    overrides: Overrides<ItemData, FeatureData>
  ) {
    this.extractData = overrides.getFeatureData
    const onSourceCreated = overrides.onSourceCreated ?? ((source) => source)

    this.layer = new Vector({
      zIndex: this.config.zIndex,
      style: this.getStyle(),
      source: onSourceCreated(this.source),
      properties: {
        isClickable: typeof this.config.onClick === 'function',
        isHoverable: typeof this.config.onHover === 'function'
      }
    })

    this.map.addLayer(this.layer)
    this.registerHandlers()
  }

  /**
   * Get the configured style for the layer.
   */
  private getStyle(style = this.config.style) {
    if (style instanceof Style || style === undefined) {
      return style
    }

    return (feature: FeatureLike, resolution: number) => {
      const data = this.extractData(feature)
      const meta = feature.getProperties() as Metadata

      const { cacheKey, value } = style({ meta, data, resolution })

      if (!this.styleCache.has(cacheKey)) {
        this.styleCache.set(cacheKey, value())
      }

      return this.styleCache.get(cacheKey) as Style
    }
  }

  /**
   * Register event handlers for the layer.
   */
  private registerHandlers() {
    if (this.config.onClick) {
      this.map.on('click', this.createHandler(this.config.onClick))
    }

    if (this.config.onHover) {
      this.map.on('pointermove', this.createHandler(this.config.onHover))
    }
  }

  /**
   * Extract the data from a feature, and enrich it with metadata and a method to set metadata.
   */
  private enrichFeature(feature: VectorFeature) {
    return {
      data: this.extractData(feature),
      meta: feature.getProperties() as Metadata,
      geometry: feature.getGeometry(),
      setMetaProperty: <Key extends keyof Metadata>(key: Key, value: Metadata[Key]) =>
        feature.set(key as string, value)
    }
  }

  /**
   * Create a handler for a UI event that extracts the data from the topmost feature
   */
  private createHandler(callback: FeatureEventHandler<FeatureData, Metadata>) {
    let previous: FeatureEvent<FeatureData, Metadata> | undefined
    return async (event: MapBrowserEvent<PointerEvent | KeyboardEvent | WheelEvent>) => {
      const coordinate = event.coordinate as [number, number]

      const features = this.map.getFeaturesAtPixel(event.pixel, {
        layerFilter: (layer) => layer === this.layer
      })

      // get first feature at the clicked pixel, ignoring features "behind" it
      const topmostFeature = features.at(0) as VectorFeature

      if (topmostFeature === undefined) {
        callback({ event: { isHit: false, coordinate, target: {} }, previous })
        previous = undefined
        return
      }

      const current = {
        isHit: true,
        coordinate,
        backgroundFeatures: features
          .slice(1) // ignore topmost, since we only care about features behind that
          .map((feature) => this.enrichFeature(feature as VectorFeature)),
        target: this.enrichFeature(topmostFeature)
      }

      callback({ event: current, previous })
      previous = current
    }
  }

  /**
   * Set the style of each feature in the layer, based on the data it represents.
   */
  public setStyle(config: StyleConfig<FeatureData, Metadata>) {
    this.layer?.setStyle(this.getStyle(config))
  }

  /**
   * Call a function for each feature in the layer, passing in the feature's data and metadata,
   * as well as a setter for the metadata.
   */
  public forEachFeature(callback: (feature: EnrichedFeature<FeatureData, Metadata>) => void) {
    this.source.forEachFeature((feature) => {
      callback(this.enrichFeature(feature))
    })
  }

  /**
   * Find a feature in the layer that matches a predicate and return the feature's data and metadata,
   * as well as a setter for the metadata.
   */
  public findFeature(predicate: (feature: EnrichedFeature<FeatureData, Metadata>) => boolean) {
    const feature = this.source.getFeatures().find((feature) => {
      return predicate(this.enrichFeature(feature))
    })

    if (feature) {
      return this.enrichFeature(feature)
    }
  }
  /**
   * Remove all data from the cluster layer.
   */
  public clear() {
    this.source.clear()
  }

  /**
   * Bounding extent of all features currently in the layer, in the map's
   * projection. Pass a destination extent to reuse an allocation; an empty
   * source yields an empty extent (see ol/extent `isEmpty`).
   */
  public getSourceExtent(into?: Extent) {
    return this.source.getExtent(into)
  }

  /**
   * Add data to the cluster layer.
   */
  public addData(items: { geometry: Geometry; data: ItemData }[]) {
    const features = items.map(
      (item) =>
        new VectorFeature({
          geometry: item.geometry,
          attributes: item.data
        })
    )

    this.source.addFeatures(features)
    return features
  }

  public removeFeatures(features: Feature<Geometry>[]) {
    this.source.removeFeatures(features)
  }

  public addGeoJsonData(geoJson: GeoJSON.FeatureCollection) {
    const features = Geo.GeoJSON.toOpenlayersFeatures(geoJson)
    this.source.addFeatures(features)
  }
}

// type helpers

type Overrides<ItemData, FeatureData = ItemData> = {
  /**
   * Hook into the source creation process to allow for customization, like adding
   * Modify and Snap interactions to the source or wrapping it in a Cluster source.
   * This method is called after the source is created, but before it is assigned
   * to the layer. Whatever source is returned will be used as the source for the layer.
   */
  onSourceCreated?: (source: VectorSource) => VectorSource

  /**
   * Extract the data from a feature. Each subclass will have a different way
   * of extracting the data from the feature, so this method must be implemented
   * by each subclass.
   */
  getFeatureData: (feature: FeatureLike) => FeatureData
}

/**
 * A style configuration for a vector layer. This can be a single style, or a function
 * that returns a function to compute the style and a key under which to store the result in the cache.
 *
 * Note that the key cache wins: if the key is the same, the function will not be called.
 * So make sure that they key maps uniquely to the style.
 */
type StyleConfig<Data, Meta extends MetaConstraint> =
  | Style
  | ((feature: StyleCallbackProps<Data, Meta>) => {
      cacheKey: string | number | boolean
      value: () => Style
    })

/**
 * Coordinate in the current map projection.
 */
type Coordinate = { coordinate: [number, number] }

/**
 * Constructor props for a vector layer.
 * @see {@link BaseVectorLayer}
 */
export type VectorLayerProps<ItemData, Meta extends MetaConstraint, FeatureData = ItemData> = {
  /**
   * The z-index of the layer. Enforced to be an enum value to prevent arbitrary
   * z-index values, and to encourage a single source of truth for all z-index values,
   * so that they are easy to reason about.
   */
  zIndex: Z_INDEX
  /**
   * @see {@link StyleConfig}
   */
  style?: StyleConfig<FeatureData, Meta>
  onClick?: FeatureEventHandler<FeatureData, Meta>
  onHover?: FeatureEventHandler<FeatureData, Meta>
}

/**
 * The metadata must be a record with string keys. The values will be inferred using generics.
 */
type MetaConstraint = Record<string, unknown>

type EnrichedFeature<Data, Meta extends MetaConstraint> = {
  data: Data
  geometry: Geometry | undefined
} & Metadata<Meta> &
  MetadataSetter<Meta>

/**
 * Every callback for an event affecting a feature will have access to feature's data and metadata,
 * the coordinate of the event, and a method to set the feature's metadata properties.
 */
type FeatureEvent<Data, Meta extends MetaConstraint> = Coordinate &
  (
    | {
        target: EnrichedFeature<Data, Meta>
        isHit: true
        backgroundFeatures: EnrichedFeature<Data, Meta>[]
      } // the partial is just so typescript doesn't claim the properties don't exist, but they'll always be undefined
    | { isHit: false; target?: Partial<EnrichedFeature<Data, Meta>> } // the partial is just so typescript doesn't claim the properties don't exist, but they'll always be undefined
  )
/**
 * The callback gives access to both the current event and the last one that occurred, so that the
 * consumer can compare the two and take action based on the difference. (For example, when removing
 * styles specific to a hover event after it has finished.)
 */
type FeatureEventHandler<Data, Meta extends MetaConstraint> = (event: {
  event: FeatureEvent<Data, Meta>
  previous: FeatureEvent<Data, Meta> | undefined
}) => void

/**
 * Metadata that represents mutable map UI state, like whether a feature is focused or emphasized.
 * This is separate from data, which is the immutable, underlying data that the feature represents.
 */
type Metadata<T extends MetaConstraint> = {
  meta: Partial<T>
}
/**
 * Metadata is particularly useful as a way to declaratively style features based on their state,
 * so that event handlers can just set metadata properties, and the layer will automatically update.
 */
type MetadataSetter<T> = {
  setMetaProperty: <Key extends keyof T>(key: Key, value: T[Key]) => void
}

type StyleCallbackProps<Data, Meta extends MetaConstraint> = {
  data: Data
  resolution: number
} & Metadata<Meta>
